import React from 'react';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import {
  CellProps,
  getI18nKey,
  isPrimitiveArrayControl,
  JsonFormsCellRendererRegistryEntry,
  JsonSchema4,
  JsonSchema7,
  RankedTester,
  rankWith,
} from '@jsonforms/core';
import { TranslateProps, withJsonFormsCellProps, withTranslateProps } from '@jsonforms/react';
import Svg from '../../../Svg';
import { RENDERER_RANK_PREFERRED_MATCH } from '../constants';

import { AppText } from '../../../AppText';

const styles = StyleSheet.create({
  selector: {
    marginTop: 12,
    marginBottom: 12,
    paddingHorizontal: 24,
    width: '100%',
  },
  checkboxRow: {
    flexDirection: 'row',
  },
  checkbox: {
    width: 32,
  },
  checkboxLabel: {
    paddingLeft: 0,
    fontSize: 16,
    fontFamily: 'roboto-regular',
  },
});

/**
 * Tester to determine if a control can be used with this cell.
 */
const tester: RankedTester = rankWith(RENDERER_RANK_PREFERRED_MATCH, isPrimitiveArrayControl);

/**
 * Base cell renderer to show a single-select enum input control.
 * @param props Control props to be hydrated by context.
 */
export const ArrayCellRenderer = (props: CellProps & TranslateProps) => {
  function collectEnum(itemField?: JsonSchema4 | JsonSchema7 | JsonSchema4[] | JsonSchema7[]): any[] {
    if (itemField === undefined) {
      return [];
    }
    if (Array.isArray(itemField)) {
      const result: any[] = [];
      for (const item of itemField) {
        result.push(collectEnum(item));
      }
      return result;
    }
    return itemField.enum ?? [];
  }

  function toggleEnum(enumValue: any) {
    let newData: any[];
    if (!Array.isArray(props.data)) {
      newData = [enumValue];
    } else if (props.data.includes(enumValue)) {
      newData = props.data.filter((v) => v !== enumValue);
    } else {
      newData = [...props.data, enumValue];
    }
    props.handleChange(props.path, newData);
  }

  return (
    <View testID={`ArrayCell-${props.id}`}>
      {collectEnum(props.schema.items).map((enumValue) => (
        <View key={enumValue} style={styles.selector}>
          <View style={styles.checkboxRow}>
            <TouchableOpacity
              style={styles.checkbox}
              onPress={() => toggleEnum(enumValue)}
              testID={`ArrayCell-${props.id}-option-${enumValue}`}
            >
              <Svg
                icon={
                  Array.isArray(props.data) && props.data.includes(enumValue)
                    ? 'square_checked'
                    : 'square_unchecked'
                }
                style={styles.checkbox}
              />
            </TouchableOpacity>
            <AppText style={styles.checkboxLabel}>
              {props.t(getI18nKey(props.schema, props.uischema, props.path, enumValue), enumValue)}
            </AppText>
          </View>
        </View>
      ))}
    </View>
  );
};

/**
 * Renderer registry entry for input control.
 * Bundles the tester and the wrapped control for use with form.
 */
export const ArrayCell: JsonFormsCellRendererRegistryEntry = {
  tester,
  cell: withJsonFormsCellProps(withTranslateProps(ArrayCellRenderer)),
};
